import React, { useState, useEffect, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { detectRegion, getIntroFlags } from "./utils/regionDetect";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Products from "./pages/Products";
import WhyTotalComposites from "./pages/WhyTotalComposites";
import BuilderResources from "./pages/BuilderResources";
import Partners from "./pages/Partners";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Order from "./pages/Order";
import OrderKeiTruck from "./pages/OrderKeiTruck";
import Expedition from "./pages/Expedition";
import Gallery from "./pages/Gallery";
import Portal from "./pages/Portal";
import AdminPanel from "./pages/AdminPanel";
import ChatWidget from "./components/ChatWidget";
import Media from "./pages/Media";
import Events from "./pages/Events";
import Articles from "./pages/Articles";
import ArticlePage from "./pages/ArticlePage";
import NotFound from "./pages/NotFound";

/* ── Bold brand name — runs on every route change ── */
const useTCBold = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const walk = (node) => {
      if (node.nodeType === 3) { // text node
        if (node.textContent.includes('TOTAL COMPOSITES')) {
          const parts = node.textContent.split('TOTAL COMPOSITES');
          if (parts.length < 2) return;
          const frag = document.createDocumentFragment();
          parts.forEach((part, i) => {
            if (part) frag.appendChild(document.createTextNode(part));
            if (i < parts.length - 1) {
              const b = document.createElement('b');
              b.className = 'tc-brand';
              b.textContent = 'TOTAL COMPOSITES';
              frag.appendChild(b);
            }
          });
          node.parentNode.replaceChild(frag, node);
        }
      } else if (
        node.nodeType === 1 &&
        !['SCRIPT','STYLE','INPUT','TEXTAREA','B','STRONG','CANVAS'].includes(node.tagName)
      ) {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    const t = setTimeout(() => walk(document.body), 300);
    return () => clearTimeout(t);
  }, [pathname]);
};

/* ── Scroll to top on route change + visitor tracking ── */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Track page visit
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: pathname }),
    }).catch(() => {});
  }, [pathname]);
  return null;
};

/* ── Page transition wrapper ── */
const PageTransition = ({ children }) => {
  const { pathname } = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [opacity, setOpacity] = useState(1);
  const isPrerendering = navigator.userAgent.includes('ReactSnap');

  useEffect(() => {
    if (isPrerendering) { setDisplayChildren(children); return; }
    setOpacity(0);
    const t1 = setTimeout(() => {
      setDisplayChildren(children);
      setOpacity(1);
    }, 180);
    return () => clearTimeout(t1);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- children intentionally excluded: new ref every render

  if (isPrerendering) return <>{children}</>;

  return (
    <div style={{ opacity, transform: opacity === 1 ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 0.25s ease, transform 0.25s ease' }}>
      {displayChildren}
    </div>
  );
};

/* ── Page-aware layout ── */
const Layout = ({ loaderDone }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  useTCBold();

  return (
    <div className="flex flex-col min-h-screen" style={{ overflowX: "hidden", maxWidth: "100vw" }}>
      <Navbar loaderDone={loaderDone} />
      <main className="flex-1" style={{ backgroundColor: '#000' }}>
        <PageTransition>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/lynx" element={<Products modelSlug="lynx" />} />
            <Route path="/wolf" element={<Products modelSlug="wolf" />} />
            <Route path="/wolverine" element={<Products modelSlug="wolverine" />} />
            <Route path="/expedition-kits" element={<Products modelSlug="expedition-kits" />} />
            <Route path="/why-total-composites" element={<WhyTotalComposites />} />
            <Route path="/builder-resources" element={<BuilderResources />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/order" element={<Order />} />
            <Route path="/order/kei-truck" element={<OrderKeiTruck />} />
            <Route path="/expedition" element={<Expedition />} />
            <Route path="/expeditions" element={<Expedition />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/portal/admin" element={<AdminPanel />} />
            <Route path="/media" element={<Media />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />
            <Route path="/events" element={<Events />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
};

/* ── Enhanced professional loader with smooth language selection ── */
const Loader = ({ onDone }) => {
  const [phase, setPhase] = useState('hold'); // hold → pause → slide → done
  const [languageSelected, setLanguageSelected] = useState(false);
  const [selectedLang, setSelectedLang] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const { switchLanguage } = useLanguage();
  const hasPreference = localStorage.getItem('preferredLanguage');
  const region = useMemo(() => detectRegion(), []);
  const introFlags = useMemo(() => getIntroFlags(region), [region]);

  useEffect(() => {
    // Phase 1: Hold logo centered with fade-in (1.8s)
    const t1 = setTimeout(() => setPhase('pause'), 1800);
    return () => clearTimeout(t1);
  }, []);
  
  useEffect(() => {
    if (phase === 'pause') {
      if (hasPreference) {
        // Returning visitor — skip language selection, auto-advance
        const t = setTimeout(() => {
          setLanguageSelected(true);
          setPhase('slide');
        }, 600);
        return () => clearTimeout(t);
      } else {
        // First visit — show language buttons
        const t = setTimeout(() => setShowButtons(true), 400);
        return () => clearTimeout(t);
      }
    }
    if (phase === 'slide' && hasPreference) {
      // Complete the auto-advance for returning visitors
      const t = setTimeout(() => { setPhase('done'); onDone(); }, 1400);
      return () => clearTimeout(t);
    }
  }, [phase, hasPreference, onDone]);

  useEffect(() => {
    // Phase 2: After language selected, graceful continuation
    if (selectedLang) {
      const t2 = setTimeout(() => {
        setShowButtons(false);
        setLanguageSelected(true);
        setPhase('slide');
      }, 800); // Longer pause for elegant selection feedback
      
      const t3 = setTimeout(() => { 
        setPhase('done'); 
        onDone(); 
      }, 2200);
      
      return () => { 
        clearTimeout(t2); 
        clearTimeout(t3); 
      };
    }
  }, [selectedLang, onDone]);

  const handleLanguageSelect = (lang) => {
    switchLanguage(lang);
    setSelectedLang(lang);
  };

  if (phase === 'done') return null;

  return (
    <div
      className="tc-loader"
      style={{
        opacity: phase === 'slide' ? 0 : 1,
        transition: phase === 'slide' ? 'opacity 0.8s ease-out' : 'opacity 0.4s ease-in',
        backgroundColor: '#000',
      }}
    >
      <img
        src="/tc_logo.png"
        alt="Total Composites"
        style={{
          filter: 'invert(1)',
          width: phase === 'slide' ? 120 : (phase === 'pause' ? '22vw' : '36vw'),
          maxWidth: phase === 'slide' ? 120 : (phase === 'pause' ? 280 : 450),
          height: 'auto',
          opacity: phase === 'hold' ? 1 : 1,
          transform: phase === 'slide'
            ? 'translateY(calc(-50vh + 38px))'
            : phase === 'pause'
            ? 'translateY(calc(-28vh))'
            : 'translateY(0)',
          transition: phase === 'pause'
            ? 'width 1.2s cubic-bezier(0.23, 1, 0.32, 1), max-width 1.2s cubic-bezier(0.23, 1, 0.32, 1), transform 1.2s cubic-bezier(0.23, 1, 0.32, 1)'
            : phase === 'slide'
            ? 'width 1.4s cubic-bezier(0.23, 1, 0.32, 1), max-width 1.4s cubic-bezier(0.23, 1, 0.32, 1), transform 1.4s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.6s ease'
            : 'none',
          willChange: 'transform, width, opacity',
        }}
      />
      
      {/* Language Selection - smooth fade-in appearance */}
      {phase === 'pause' && (
        <div
          style={{
            position: 'absolute',
            top: '56%',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '32px',
            opacity: showButtons ? 1 : 0,
            transition: 'opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* English Flag */}
          <button
            onClick={() => handleLanguageSelect('en')}
            style={{
              background: selectedLang === 'en' ? 'rgba(232, 82, 10, 0.08)' : 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(10px)',
              border: selectedLang === 'en' ? '3px solid #E8520A' : '2px solid rgba(255,255,255,0.15)',
              borderRadius: '16px',
              padding: '24px 32px',
              cursor: selectedLang ? 'default' : 'pointer',
              transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              opacity: selectedLang === null ? 1 : (selectedLang === 'en' ? 1 : 0),
              transform: selectedLang === 'en' ? 'scale(1.12)' : 'scale(1)',
              boxShadow: selectedLang === 'en' ? '0 0 40px rgba(232, 82, 10, 0.3)' : '0 4px 20px rgba(0,0,0,0.3)',
              pointerEvents: selectedLang ? 'none' : 'auto',
            }}
            onMouseEnter={(e) => {
              if (!selectedLang) {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(255,255,255,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!selectedLang) {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.3)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
              }
            }}
          >
            <div style={{ 
              width: '96px', 
              height: '64px', 
              borderRadius: '8px', 
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img 
                src={introFlags.en.src}
                alt={introFlags.en.alt}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <span style={{ 
              color: '#fff', 
              fontSize: '13px', 
              fontWeight: '700', 
              textTransform: 'uppercase', 
              letterSpacing: '0.15em',
              opacity: 0.9,
            }}>English</span>
          </button>

          {/* French / Fran&ccedil;ais */}
          <button
            onClick={() => handleLanguageSelect('fr')}
            style={{
              background: selectedLang === 'fr' ? 'rgba(232, 82, 10, 0.08)' : 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(10px)',
              border: selectedLang === 'fr' ? '3px solid #E8520A' : '2px solid rgba(255,255,255,0.15)',
              borderRadius: '16px',
              padding: '24px 32px',
              cursor: selectedLang ? 'default' : 'pointer',
              transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              opacity: selectedLang === null ? 1 : (selectedLang === 'fr' ? 1 : 0),
              transform: selectedLang === 'fr' ? 'scale(1.12)' : 'scale(1)',
              boxShadow: selectedLang === 'fr' ? '0 0 40px rgba(232, 82, 10, 0.3)' : '0 4px 20px rgba(0,0,0,0.3)',
              pointerEvents: selectedLang ? 'none' : 'auto',
            }}
            onMouseEnter={(e) => {
              if (!selectedLang) {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(255,255,255,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!selectedLang) {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.3)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
              }
            }}
          >
            <div style={{ 
              width: '96px', 
              height: '64px', 
              borderRadius: '8px', 
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img 
                src={introFlags.fr.src}
                alt={introFlags.fr.alt}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <span style={{ 
              color: '#fff', 
              fontSize: '13px', 
              fontWeight: '700', 
              textTransform: 'uppercase', 
              letterSpacing: '0.15em',
              opacity: 0.9,
            }}>Fran&ccedil;ais</span>
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Language preference banner for returning visitors ── */
const LanguageBanner = () => {
  const { language, switchLanguage } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hasPreference = localStorage.getItem('preferredLanguage');

  useEffect(() => {
    // Only show for returning users who already have a saved preference
    if (!hasPreference) return;
    // Don't show again if dismissed this session
    if (sessionStorage.getItem('bannerDismissed')) return;
    const t = setTimeout(() => setVisible(true), 3000);
    const autoDismiss = setTimeout(() => {
      setDismissed(true);
      sessionStorage.setItem('bannerDismissed', '1');
    }, 6000);
    return () => { clearTimeout(t); clearTimeout(autoDismiss); };
  }, [hasPreference]);

  const handleReset = () => {
    localStorage.removeItem('preferredLanguage');
    sessionStorage.removeItem('bannerDismissed');
    window.location.reload(); // Reload to show intro
  };

  const handleSwitch = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    switchLanguage(newLang);
    setDismissed(true);
    sessionStorage.setItem('bannerDismissed', '1');
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('bannerDismissed', '1');
  };

  if (!hasPreference || !visible || dismissed) return null;

  return (
    <div
      data-testid="language-banner"
      style={{
        position: 'fixed',
        bottom: '90px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 20px',
        borderRadius: '50px',
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        animation: 'bannerSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: '#fff', fontSize: '13px', fontWeight: '500', opacity: 0.9 }}>
        {language === 'fr' ? 'Site en fran\u00e7ais' : 'Viewing in English'}
      </span>
      <span style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }} />
      <button
        data-testid="language-banner-switch"
        onClick={handleSwitch}
        style={{
          background: 'none',
          border: 'none',
          color: '#E8520A',
          fontSize: '13px',
          fontWeight: '700',
          cursor: 'pointer',
          padding: '2px 4px',
          letterSpacing: '0.03em',
        }}
      >
        {language === 'fr' ? 'Switch to English' : 'Passer en fran\u00e7ais'}
      </button>
      <button
        data-testid="language-banner-dismiss"
        onClick={handleDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '16px',
          cursor: 'pointer',
          padding: '2px 6px',
          lineHeight: 1,
        }}
        aria-label="Dismiss"
      >
        &times;
      </button>
      <style>{`
        @keyframes bannerSlideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

function App() {
  const isPrerendering = navigator.userAgent.includes('ReactSnap');
  const [loaderDone, setLoaderDone] = useState(isPrerendering);

  return (
    <HelmetProvider>
    <LanguageProvider>
      <CurrencyProvider>
      <AuthProvider>
        <BrowserRouter>
          {!loaderDone && <Loader onDone={() => setLoaderDone(true)} />}
          <ScrollToTop />
          <Layout loaderDone={loaderDone} />
          {!isPrerendering && <LanguageBanner />}
        </BrowserRouter>
      </AuthProvider>
      </CurrencyProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
